import React, {Fragment, useCallback, useMemo} from 'react';
import {Button, Checkbox, Col, Form, Input, Radio, Row, Select} from 'antd';
import {GUTTER_MD, MARGIN_MD, SPAN_FULL, SPAN_HALF} from '../../../../../styles/constants';
import {compose} from 'redux';
import {translate} from 'react-i18next';
import {connect} from 'react-redux';
import FileInput from '../../../../../components/file-input';
import {
    changeFileMappingWizardCsvForm,
    setFileMappingWizardCsvFormFileFormat,
    submitFileMappingWizardCsvForm,
    submitPluginFileMappingWizardFiles
} from './actions';
import {getFileMappingWizardCsvRowListCsvRows, showFileMappingWizardCsvRowList} from "../csv-row-list/actions";
import {convertFileMappingWizardPluginToCsv, getFileMappingWizardPlugin, resetFields } from "../actions";
import Call from "../../../../../hocs/call";
import {DataLanguageConsumer} from "../../../../../contexts/DataLanguage";
import {isExtensionInvalid} from "../../../../../utils/artefactValidators";

const mapStateToProps = state => ({
    appLanguage: state.app.language,
    nodeId: state.app.endpointId,
    cubeId: state.scenes.dataManager.fileMapping.components.wizard.shared.cubeId,
    csvForm: state.scenes.dataManager.fileMapping.components.wizard.shared.csvForm,
    csvHeader: state.scenes.dataManager.fileMapping.components.wizard.shared.csvHeader,
    fileFormat: state.scenes.dataManager.fileMapping.components.wizard.shared.fileFormat,
    csvServerPath: state.scenes.dataManager.fileMapping.components.wizard.shared.csvServerPath,
    fileServerPath: state.scenes.dataManager.fileMapping.components.wizard.shared.fileServerPath,
    secondServerPath: state.scenes.dataManager.fileMapping.components.wizard.shared.secondServerPath,
    thirdServerPath: state.scenes.dataManager.fileMapping.components.wizard.shared.thirdServerPath,
    configuredPlugins: state.scenes.dataManager.fileMapping.components.wizard.shared.configuredPlugins,
    permissions: state.app.user.permissions,
});

const mapDispatchToProps = dispatch => ({
    onChange: fields => dispatch(changeFileMappingWizardCsvForm(fields)),
    onFileFormatSet: fileFormat => dispatch(setFileMappingWizardCsvFormFileFormat(fileFormat)),
    onSubmit: file => dispatch(submitFileMappingWizardCsvForm(file)),
    onSubmitPlugin: (cubeId, pluginUid, file, transformFile, processFile) => dispatch(submitPluginFileMappingWizardFiles(cubeId, pluginUid, file, transformFile, processFile)),
    onCsvRowListShow: () => dispatch(showFileMappingWizardCsvRowList()),
    getConfiguredPlugins: ({nodeId}) => dispatch(getFileMappingWizardPlugin(nodeId)),
    onConvertPluginToCsv: ({
                               pluginUid,
                               fileServerPath,
                               transformFileServerPath,
                               processFileServerPath,
                               lang,
                               dllName,
                               cubeId
                           }) => dispatch(convertFileMappingWizardPluginToCsv(pluginUid, fileServerPath, transformFileServerPath, processFileServerPath, lang, ".csv", dllName, cubeId)),
    forceCsvRows: filePath => dispatch(getFileMappingWizardCsvRowListCsvRows(1, 0, [], undefined, undefined, ";", '"', true, filePath)),
    onResetFields: () => dispatch(resetFields()),
});

const mapPropsToFields = ({csvForm}) => ({
    file: Form.createFormField({value: csvForm.file}),
    separator: Form.createFormField({value: csvForm.separator}),
    delimiter: Form.createFormField({value: csvForm.delimiter}),
    hasHeader: Form.createFormField({value: csvForm.hasHeader}),
    hasDotStatFormat: Form.createFormField({value: csvForm.hasDotStatFormat}),
    configuredPlugin: Form.createFormField({value: csvForm.configuredPlugin}),
    transformFileInput: Form.createFormField({value: csvForm.transformFileInput || null}),
    processFileInput: Form.createFormField({value: csvForm.processFileInput || null}),
})

// TODO: prevent multiple changes relative to same value
const onFieldsChange = (props, fields) => props.onChange(fields);

const FileMappingWizardCsvForm = ({
                                      t,
                                      form,
                                      fileFormat,
                                      csvForm,
                                      csvHeader,
                                      nodeId,
                                      csvServerPath,
                                      fileServerPath,
                                      secondServerPath,
                                      thirdServerPath,
                                      appLanguage,
                                      configuredPlugins,
                                      onFileFormatSet,
                                      getConfiguredPlugins,
                                      onConvertPluginToCsv,
                                      onSubmit,
                                      onSubmitPlugin,
                                      onCsvRowListShow,
                                      permissions,
                                      cubeId,
                                      onResetFields
                                  }) => {

                                  
    const hasPermissionForPlugins = useMemo(() => permissions.functionality.includes("plugins"), [permissions])
    const selectedPlugin = useMemo(() => {
        if (hasPermissionForPlugins && csvForm.configuredPlugin) {
            return configuredPlugins.find(plugin => plugin.PluginUid === csvForm.configuredPlugin)
        }
        return null
    }, [csvForm.configuredPlugin]);
    
    const transformFileServerPath = useMemo(() => {
        if (!secondServerPath || !selectedPlugin || !selectedPlugin.TransformFileEnabled) {
            return null;
        }
        return secondServerPath;
    }, [secondServerPath, selectedPlugin]);

    const processFileServerPath = useMemo(() => {
        if (!secondServerPath || !selectedPlugin || !selectedPlugin.ProcessFileEnabled) {
            return null;
        }
        if (secondServerPath && !selectedPlugin.TransformFileEnabled) {
            return secondServerPath;
        }
        return thirdServerPath;
    }, [secondServerPath, thirdServerPath, selectedPlugin]);

    const resetFieldsChangePlugin = useCallback(() => {
        onResetFields();
        form.setFieldsValue({
            file: null,
            separator: null,
            delimiter: null,
            hasHeader: null,
            hasDotStatFormat: null,
            transformFileInput: null,
            processFileInput: null,
        });
    }, [onResetFields, form]);


    return (
        <DataLanguageConsumer>
            {dataLanguage => {
                const lang = dataLanguage || appLanguage;
                return (
        <Call cb={getConfiguredPlugins} cbParam={{nodeId}}
              disabled={fileFormat !== 'plugin' || !!configuredPlugins || !hasPermissionForPlugins}>
            <Call cb={onConvertPluginToCsv}
                  cbParam={{
                      pluginUid: selectedPlugin ? selectedPlugin.PluginUid : null,
                      fileServerPath,
                      transformFileServerPath,
                      processFileServerPath,
                      lang,
                      dllName: selectedPlugin ? selectedPlugin.DllName : null,
                      cubeId
                  }}
                  disabled={fileFormat !== 'plugin' || csvServerPath !== null || fileServerPath === null || !selectedPlugin ||
                      (selectedPlugin.TransformFileEnabled && transformFileServerPath === null) ||
                      (selectedPlugin.ProcessFileEnabled && processFileServerPath === null)}>
                <Form className="advanced-form">
                    <Row style={{marginBottom: MARGIN_MD}} gutter={GUTTER_MD}>
                        <Col span={SPAN_FULL}>
                            <Radio.Group
                                defaultValue="csv"
                                value={fileFormat}
                                onChange={({target}) => onFileFormatSet(target.value)}
                            >
                                <Radio.Button value="csv">
                                    CSV
                                </Radio.Button>
                                {hasPermissionForPlugins && <Radio.Button value="plugin">
                                    {t('data.csv.otherFiles.label')}
                                </Radio.Button>}
                            </Radio.Group>
                        </Col>
                    </Row>
                    {fileFormat === 'plugin' && (<Row style={{marginBottom: MARGIN_MD}} gutter={GUTTER_MD}>
                        <Row style={{marginBottom: MARGIN_MD}} gutter={GUTTER_MD}>
                            <Col span={SPAN_FULL}>
                                <Form.Item className="form-item-required" label={t('data.csv.plugin.label')}>
                                    {form.getFieldDecorator('configuredPlugin')(
                                        <Select
                                            showSearch
                                            filterOption={(inputValue, {props}) =>
                                                props.title.toLowerCase().includes(inputValue.toLowerCase())
                                            }
                                            title={
                                                csvForm.configuredPlugin
                                            }
                                            onChange={(e) => {
                                                if(e !== form.getFieldValue('configuredPlugin')){
                                                    resetFieldsChangePlugin()
                                                }
                                            }}
                                            >
                                            {configuredPlugins
                                                ? configuredPlugins.map(plugin => {
                                                    return (
                                                        <Select.Option
                                                            key={plugin.PluginUid}
                                                            value={plugin.PluginUid}
                                                            title={plugin.PluginName[lang]}
                                                        >
                                                            {plugin.PluginName[lang]}
                                                        </Select.Option>
                                                    );
                                                })
                                                : null
                                            }
                                        </Select>
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>
                    </Row>)}
                    {(fileFormat !== 'plugin' || (fileFormat === 'plugin' && !!selectedPlugin)) && (
                        <Row style={{marginBottom: MARGIN_MD}} gutter={GUTTER_MD}>
                            <Col span={SPAN_FULL}>
                                <Form.Item className="form-item-required" label={t('data.csv.file.label')}
                                           validateStatus={fileFormat === 'plugin' && csvForm.file && isExtensionInvalid(csvForm.file.name, selectedPlugin.InputExtensions) ? "warning" : null}
                                           help={fileFormat === 'plugin' && csvForm.file && isExtensionInvalid(csvForm.file.name, selectedPlugin.InputExtensions) ? t("scenes.utilities.plugin.modal.input.messages.invalidExtension") : null}>
                                    {form.getFieldDecorator('file')(
                                        <FileInput
                                            accept={fileFormat === 'csv' ? `.${fileFormat}` : (selectedPlugin ? selectedPlugin.InputExtensions : []).join(",")}
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>)}
                    {fileFormat === 'plugin' && !!selectedPlugin && ['TransformFileEnabled', 'ProcessFileEnabled'].map(fieldName => {
                        if (!selectedPlugin || !selectedPlugin[fieldName]) {
                            return null;
                        }
                        const lowercaseField = `${fieldName[0].toLowerCase()}${fieldName.substring(1)}`.replace("Enabled", "Input");
                        const isProcess = fieldName.includes("Process");
                        return (
                            <Row style={{marginBottom: MARGIN_MD}} gutter={GUTTER_MD} key={fieldName}>
                                <Col span={SPAN_FULL}>
                                    <Form.Item className="form-item-required"
                                               label={t(`data.csv.${lowercaseField}.label`)}
                                               validateStatus={((isProcess && csvForm.processFileInput) || (!isProcess && csvForm.transformFileInput)) &&
                                               isExtensionInvalid(isProcess ? csvForm.processFileInput.name : csvForm.transformFileInput.name,
                                                   isProcess ? [selectedPlugin.ProcessExtension] : [selectedPlugin.TransformExtension]) ? "warning" : null}
                                               help={((isProcess && csvForm.processFileInput) || (!isProcess && csvForm.transformFileInput)) &&
                                               isExtensionInvalid(isProcess ? csvForm.processFileInput.name : csvForm.transformFileInput.name,
                                                   isProcess ? [selectedPlugin.ProcessExtension] : [selectedPlugin.TransformExtension]) ? t("scenes.utilities.plugin.modal.input.messages.invalidExtension") : null}
                                    >
                                        {form.getFieldDecorator(lowercaseField)(
                                            <FileInput
                                                accept={fieldName.includes("Process") ? selectedPlugin.ProcessExtension : selectedPlugin.TransformExtension}
                                            />
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>
                        )
                    })}
                    {fileFormat === 'csv' && (
                        <Fragment>
                            <Row style={{marginBottom: MARGIN_MD}} gutter={GUTTER_MD}>
                                <Col span={SPAN_HALF}>
                                    <Form.Item className="form-item-required"
                                               label={t('data.csv.separator.label')}>
                                        {form.getFieldDecorator('separator')(<Input maxLength={1}/>)}
                                    </Form.Item>
                                </Col>
                                <Col span={SPAN_HALF}>
                                    <Form.Item label={t('data.csv.delimiter.label')}>
                                        {form.getFieldDecorator('delimiter')(<Input maxLength={1}/>)}
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row style={{marginBottom: MARGIN_MD}} gutter={GUTTER_MD}>
                                <Col span={SPAN_HALF}>
                                    <Form.Item className="form-item-required"
                                               label={t('data.csv.hasHeader.label')}>
                                        {form.getFieldDecorator('hasHeader', {valuePropName: 'checked'})(
                                            <Checkbox/>)}
                                    </Form.Item>
                                </Col>
                                <Col span={SPAN_HALF}>
                                    <Form.Item className="form-item-required"
                                               label={t('data.csv.hasDotStatFormat.label')}>
                                        {form.getFieldDecorator('hasDotStatFormat', {valuePropName: 'checked'})(
                                            <Checkbox/>)}
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Fragment>
                    )}
                    <Row type="flex" gutter={GUTTER_MD}>
                        <Col>
                            <Button
                                disabled={
                                    (fileFormat === 'csv' && (csvForm.file === null ||
                                        (csvForm.separator === null || csvForm.separator.length !== 1))) || (fileFormat === 'plugin' && (!selectedPlugin || (!csvForm.file ||
                                        isExtensionInvalid(csvForm.file.name, selectedPlugin.InputExtensions) ||
                                        (selectedPlugin.TransformFileEnabled && (!csvForm.transformFileInput || isExtensionInvalid(csvForm.transformFileInput.name, [selectedPlugin.TransformExtension]))) ||
                                        (selectedPlugin.ProcessFileEnabled && (!csvForm.processFileInput || isExtensionInvalid(csvForm.processFileInput.name, [selectedPlugin.ProcessExtension]))))))
                                }
                                icon="upload"
                                type="primary"
                                onClick={() => fileFormat === 'csv' ?
                                    onSubmit(csvForm.file) :
                                    onSubmitPlugin(cubeId, selectedPlugin ? selectedPlugin.PluginUid : null, csvForm.file, csvForm.transformFileInput ? csvForm.transformFileInput : null, csvForm.processFileInput ? csvForm.processFileInput : null)}
                            >
                                {t('data.csv.uploadButton.label')}
                            </Button>
                            
                        </Col>
                        <Col>
                            <Button
                                onClick={onCsvRowListShow}
                                icon="table"
                                disabled={csvHeader === null}
                                style={{width: '100%'}}>
                                {t('scenes.dataManager.fileMapping.wizard.csvForm.buttons.csvRowListShow')}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Call>
        </Call>
                )}}
        </DataLanguageConsumer>
    )
};

export default compose(
    translate(),
    connect(mapStateToProps, mapDispatchToProps),
    Form.create({mapPropsToFields, onFieldsChange})
)(FileMappingWizardCsvForm);
